import { useMemo } from 'react';
import { useStyletron } from 'baseui';
import { StyleObject } from 'styletron-react';
import _mapValues from 'lodash/mapValues';
import { Tone } from './types';

export const TOKENS = {
	color: {
		primary: '#1E40AF',
		primaryBg: '#EFF6FF',
		background: '#F8FAFC',
		card: '#FFFFFF',
		border: '#E2E8F0',
		text: '#0F172A',
		muted: '#475569',
		warningBg: '#FFFBEB',
		warningText: '#B45309',
		warningBorder: '#D97706',
		danger: '#DC2626',
		dangerBg: '#FEF2F2',
		success: '#15803D',
		successBg: '#F0FDF4',
		neutralBg: '#F1F5F9'
	},
	space: {
		xs: '8px',
		sm: '12px',
		md: '16px',
		lg: '24px',
		xl: '32px'
	},
	radius: '8px',
	font: {
		mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
		sizeXs: '12px',
		sizeSm: '13px',
		sizeMd: '14px',
		sizeLg: '16px',
		sizeXl: '20px'
	}
} as const;

const { color, space, font, radius } = TOKENS;

export const TONE_STYLES: Record<Tone, StyleObject> = {
	neutral: { backgroundColor: color.neutralBg, color: color.text, borderColor: color.border },
	success: { backgroundColor: color.successBg, color: color.success, borderColor: color.success },
	warning: { backgroundColor: color.warningBg, color: color.warningText, borderColor: color.warningBorder },
	danger: { backgroundColor: color.dangerBg, color: color.danger, borderColor: color.danger },
	info: { backgroundColor: color.primaryBg, color: color.primary, borderColor: color.primary }
};

const mono: StyleObject = {
	fontFamily: font.mono,
	fontVariantNumeric: 'tabular-nums'
};

const border: StyleObject = {
	borderWidth: '1px',
	borderStyle: 'solid',
	borderColor: color.border
};

const padding = (vertical: string, horizontal: string): StyleObject => ({
	paddingTop: vertical,
	paddingBottom: vertical,
	paddingLeft: horizontal,
	paddingRight: horizontal
});

// Longhand properties only: Styletron's atomic classes don't resolve shorthand/longhand overrides by order
const card: StyleObject = {
	...border,
	backgroundColor: color.card,
	borderRadius: radius,
	...padding(space.md, space.md)
};

export const STYLES: Record<string, StyleObject> = {
	// The app shell is a fixed, overflow-hidden box, so the page scrolls itself below the app bar + breadcrumbs
	scrollArea: {
		height: 'calc(100vh - 110px)',
		overflowY: 'auto'
	},
	page: {
		backgroundColor: color.background,
		color: color.text,
		padding: space.lg,
		display: 'flex',
		flexDirection: 'column',
		gap: space.md,
		maxWidth: '1200px',
		width: '100%',
		boxSizing: 'border-box',
		marginLeft: 'auto',
		marginRight: 'auto'
	},
	title: {
		margin: 0,
		fontSize: font.sizeXl,
		fontWeight: 600,
		color: color.text
	},
	subtitle: {
		margin: 0,
		fontSize: font.sizeSm,
		color: color.muted
	},
	card,
	stack: {
		display: 'flex',
		flexDirection: 'column',
		gap: space.md
	},
	sectionTitle: {
		margin: `0 0 ${space.sm} 0`,
		fontSize: font.sizeLg,
		fontWeight: 600,
		color: color.text
	},
	mono,
	srOnly: {
		position: 'absolute',
		width: '1px',
		height: '1px',
		padding: 0,
		margin: '-1px',
		overflow: 'hidden',
		clip: 'rect(0 0 0 0)',
		whiteSpace: 'nowrap',
		border: 0
	},
	muted: {
		color: color.muted,
		fontSize: font.sizeSm
	},

	// step rail
	rail: {
		display: 'flex',
		alignItems: 'center',
		gap: space.xs,
		listStyle: 'none',
		margin: 0,
		padding: 0,
		flexWrap: 'wrap'
	},
	railItem: {
		display: 'flex',
		alignItems: 'center',
		gap: space.xs,
		fontSize: font.sizeMd,
		color: color.muted
	},
	railItemCurrent: {
		color: color.primary,
		fontWeight: 600
	},
	railItemDone: {
		color: color.text
	},
	railIndex: {
		width: '24px',
		height: '24px',
		borderRadius: '50%',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: font.sizeXs,
		fontWeight: 600,
		...border,
		backgroundColor: color.card,
		...mono
	},
	railIndexCurrent: {
		backgroundColor: color.primary,
		borderColor: color.primary,
		color: color.card
	},
	railIndexDone: {
		backgroundColor: color.successBg,
		borderColor: color.success,
		color: color.success
	},
	railDivider: {
		width: space.lg,
		height: '1px',
		backgroundColor: color.border
	},

	// context bar
	contextBar: {
		...card,
		position: 'sticky',
		top: 0,
		zIndex: 2,
		display: 'flex',
		alignItems: 'center',
		gap: space.md,
		paddingTop: space.xs,
		paddingBottom: space.xs,
		flexWrap: 'wrap'
	},
	contextItem: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		fontSize: font.sizeSm
	},
	contextReason: {
		maxWidth: '420px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	},
	contextLabel: {
		fontSize: font.sizeXs,
		color: color.muted,
		textTransform: 'uppercase',
		letterSpacing: '0.04em'
	},
	contextSpacer: {
		flexGrow: 1
	},

	// form
	formGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
		columnGap: space.md
	},
	fieldError: {
		color: color.danger,
		fontSize: font.sizeSm,
		marginTop: '-4px',
		marginBottom: space.sm
	},
	fieldHint: {
		color: color.muted,
		fontSize: font.sizeXs,
		marginTop: '-4px',
		marginBottom: space.sm
	},
	actionsRow: {
		display: 'flex',
		alignItems: 'center',
		gap: space.sm,
		flexWrap: 'wrap'
	},

	// status line
	status: {
		display: 'flex',
		alignItems: 'center',
		gap: space.xs,
		fontSize: font.sizeMd,
		color: color.text,
		minHeight: '24px'
	},

	// notices
	notice: {
		borderWidth: '1px',
		borderStyle: 'solid',
		borderRadius: radius,
		padding: `${space.sm} ${space.md}`,
		fontSize: font.sizeMd,
		lineHeight: '20px'
	},
	noticeList: {
		margin: `${space.xs} 0 0 0`,
		paddingLeft: space.lg
	},
	noticeTitle: {
		fontWeight: 600,
		marginRight: space.xs
	},

	// badges and chips
	badge: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '4px',
		borderWidth: '1px',
		borderStyle: 'solid',
		borderRadius: '4px',
		padding: '1px 8px',
		fontSize: font.sizeXs,
		fontWeight: 600,
		lineHeight: '18px',
		whiteSpace: 'nowrap'
	},

	// candidate table
	tableWrap: {
		overflowX: 'auto',
		border: `1px solid ${color.border}`,
		borderRadius: radius,
		backgroundColor: color.card
	},
	table: {
		width: '100%',
		borderCollapse: 'collapse',
		fontSize: font.sizeMd
	},
	th: {
		textAlign: 'left',
		padding: `${space.xs} ${space.sm}`,
		fontSize: font.sizeXs,
		fontWeight: 600,
		color: color.muted,
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
		borderBottom: `1px solid ${color.border}`,
		backgroundColor: color.neutralBg
	},
	td: {
		padding: `${space.xs} ${space.sm}`,
		borderBottom: `1px solid ${color.border}`,
		verticalAlign: 'middle'
	},
	tdUid: {
		...mono,
		fontWeight: 600,
		textAlign: 'left'
	},
	tdRight: {
		textAlign: 'right'
	},
	emptyState: {
		...card,
		textAlign: 'center',
		...padding(space.xl, space.xl)
	},

	// data view
	dataHeader: {
		...card,
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'flex-start',
		gap: space.lg
	},
	headerMeta: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: space.lg,
		flexGrow: 1
	},
	sourceHeader: {
		display: 'flex',
		alignItems: 'baseline',
		gap: space.sm,
		flexWrap: 'wrap',
		margin: `${space.xs} 0`
	},
	sourceTitle: {
		margin: 0,
		fontSize: font.sizeLg,
		fontWeight: 600
	},
	groupGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 480px), 1fr))',
		gap: space.md
	},
	groupTitle: {
		margin: `0 0 ${space.sm} 0`,
		fontSize: font.sizeMd,
		fontWeight: 600,
		textTransform: 'capitalize'
	},
	kvGrid: {
		display: 'grid',
		gridTemplateColumns: 'minmax(120px, 2fr) minmax(0, 3fr)',
		columnGap: space.md,
		rowGap: space.xs,
		margin: 0
	},
	kvKey: {
		margin: 0,
		fontSize: font.sizeSm,
		color: color.muted,
		paddingTop: '6px',
		wordBreak: 'break-word'
	},
	kvValue: {
		margin: 0,
		display: 'flex',
		alignItems: 'center',
		gap: space.xs,
		flexWrap: 'wrap',
		minWidth: 0
	},
	kvValueText: {
		...mono,
		fontSize: font.sizeMd,
		color: color.text,
		wordBreak: 'break-all'
	},
	kvValueEmpty: {
		...mono,
		fontSize: font.sizeMd,
		color: color.muted
	}
};

export const mergeStyles = (...styles: StyleObject[]): StyleObject => styles.reduce((merged, style) => ({ ...merged, ...style }), {});

export default function useStyles() {
	const [css] = useStyletron();
	const classes = useMemo(() => _mapValues(STYLES, (style: StyleObject) => css(style)), [css]);
	return { css, classes };
}
